import React from 'react';
import Button from '@amzn/awsui-components-console/button';
import Skeleton from '@amzn/awsui-components-console/skeleton';
import Container from '@amzn/awsui-components-console/container';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Box from '@amzn/awsui-components-console/box';
import { Namespace } from 'src/generated/graphql';
import InfoTooltip from 'src/common/ui/InfoTooltip';
import NamespaceMetadataCard from 'src/features/namespace/components/NamespaceMetadataCard';
import { buildBindleHref, buildOwnerHref } from 'src/features/namespace/utils';

const NAMESPACE_TOOLTIP =
  'A namespace organizes string groups for a specific product or team.';

interface NamespaceHeaderProps {
  namespace: Namespace | undefined;
  onCreateStringGroup: () => void;
  /**
   * Whether the caller holds SOURCE_MANAGEMENT on this namespace. Creating a string
   * group is a source mutation, so the button is disabled without it.
   *
   * Required rather than defaulted to true: a new call site that forgets to pass it
   * should fail to compile, not quietly ship an ungated button.
   */
  canCreateStringGroup: boolean;

  /**
   * Whether the permission answer is still in flight. Gated controls show a skeleton rather
   * than a disabled state, so they never claim a missing permission before it is known.
   */
  permissionsLoading: boolean;
}

/** Displays namespace metadata in a header card with key-value pairs. */
const NamespaceHeader: React.FC<NamespaceHeaderProps> = ({
  namespace,
  onCreateStringGroup,
  canCreateStringGroup,
  permissionsLoading,
}) => {
  const bindleHref = buildBindleHref(namespace?.bindleResourceId);

  return (
    <SpaceBetween size="s">
      <Container>
        <Box display="inline-block">
          <SpaceBetween size="xs">
            <Box variant="h1" fontSize="heading-xl" fontWeight="bold">
              <SpaceBetween direction="horizontal" size="xs" alignItems="start">
                <Box variant="span" fontSize="heading-xl" fontWeight="bold">
                  {namespace?.namespaceId}
                </Box>
                <Box margin={{ top: 'xs' }}>
                  <InfoTooltip content={NAMESPACE_TOOLTIP} />
                </Box>
              </SpaceBetween>
            </Box>
            <Box variant="p" color="text-body-secondary">
              Create and manage your string groups here.
            </Box>
            <Box
              {...(namespace?.defaultCxFamily && { margin: { top: 'xxxl' } })}
              padding={{ top: 'xxxl' }}
            >
              {/*
                No tooltip or helper text on the disabled state: permissions are
                managed in Bindles, which the metadata card already links to, and
                the reason a control is disabled is not something this page can
                explain accurately (it could be a missing grant, or a sync that has
                not caught up with one).
              */}
              {/*
                Skeleton rather than a Button with `loading`: the in-button spinner means "an
                action is being initiated", and nothing has been - this is first page load.
              */}
              {permissionsLoading ? (
                <>
                  <Skeleton
                    display="inline-block"
                    height="32px"
                    width="170px"
                  />
                </>
              ) : (
                <Button
                  variant="primary"
                  onClick={onCreateStringGroup}
                  disabled={!canCreateStringGroup}
                >
                  Create string group
                </Button>
              )}
            </Box>
          </SpaceBetween>
        </Box>

        <Box float="right" padding="xs" margin={{ bottom: 'xxl' }}>
          <NamespaceMetadataCard
            cxFamily={namespace?.defaultCxFamily}
            bindleLabel={namespace?.bindleResourceId}
            bindleHref={bindleHref}
            ownerHref={buildOwnerHref(namespace?.owner, bindleHref)}
            createdAt={namespace?.createdAt}
            createdBy={namespace?.createdBy}
          />
        </Box>
      </Container>
    </SpaceBetween>
  );
};

export default NamespaceHeader;
